<template>
  <div ref="mainContainer" class="batchdelete">
    <div v-if="isLoading" class="batchdelete-content loading">
      <span class="icon is-large">
        <i class="fa-solid fa-spinner fa-spin fa-2x" />
      </span>
      <span v-if="showLoaderHelpText" class="tag is-primary is-medium mt-5">
        It might take a while if there's a lot of datafiles under this
        {{ pluralize(modelName, 1) }}!
      </span>
    </div>
    <div v-else class="batchdelete-content">
      <div v-if="selectedDatasetsCount === 0" class="mb-4">
        <div class="notification is-warning is-fullwidth">
          <span class="icon">
            <i class="fa-solid fa-info-circle" />
          </span>
          <span class="text has-text-weight-semibold">
            Please select datasets to remove from disk!
          </span>
        </div>
      </div>

      <div id="section">
        <ToggleSection
          v-if="Boolean(studiesByProjectId)"
          label="Actively used studies"
          :collapsed="true"
        >
          <div class="my-4 px-5 has-text-primary is-size-6">
            <i class="fa-solid fa-info-circle is-size-6"></i>
            <i class="is-size-6">
              In case some studies are still actively used, please select them
              in the list below. Datasets associated to the selected studies
              <b>will not be deleted</b>
              and be kept online instead.
            </i>
          </div>
          <div class="content-item-list-container mx-5 mb-5">
            <table class="table is-fullwidth is-bordered">
              <thead class="sticky at-top">
                <tr>
                  <th>
                    {{
                      `${projectsCount} ${pluralize('Project', projectsCount)}`
                    }}
                  </th>
                  <th>
                    {{ `${studiesCount} ${pluralize('Study', studiesCount)}` }}
                  </th>
                  <th
                    v-for="storagevolume in modifyableStoragevolumes"
                    :key="storagevolume.id"
                  >
                    <span class="text-ellipsis" :title="storagevolume.name">
                      {{ storagevolume.name }}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <template
                  v-for="[projectId, project] in Object.entries(
                    studiesByProjectId,
                  )"
                  :key="projectId"
                >
                  <tr
                    v-for="(study, studyIdx) in Object.values(project.studies)"
                    :key="study.id"
                  >
                    <td
                      v-if="studyIdx === 0"
                      :rowspan="Object.keys(project.studies).length"
                      class="vertically-centered"
                    >
                      <div class="is-flex">
                        <span>
                          {{ record.projects[projectId].name }}
                        </span>
                        <i class="ml-2">
                          ({{
                            `${datafilesCount['projects'][projectId].datafiles} datafiles in ${datafilesCount['projects'][projectId].datasets} datasets`
                          }})
                        </i>
                      </div>
                    </td>
                    <td>
                      <div class="is-flex">
                        <span>
                          {{ study.name }}
                        </span>
                        <i class="ml-2">
                          ({{
                            `${
                              datafilesCount['studies'][study.id].datafiles
                            } datafiles in ${
                              datafilesCount['studies'][study.id].datasets
                            } datasets`
                          }})
                        </i>
                      </div>
                    </td>
                    <td
                      v-for="storagevolume in Object.values(
                        study.storagevolumes,
                      )"
                      :key="`${study.id}_${storagevolume.id}`"
                    >
                      <span class="_checkbox" style="text-align: center">
                        <input
                          type="checkbox"
                          :checked="checkIfStoragevolumeSelected(storagevolume)"
                          @change="
                            toggleTopLevelStoragevolumeSelection(
                              {
                                projectId,
                                studyId: study.id,
                                storagevolumeId: storagevolume.id,
                              },
                              $event,
                            )
                          "
                        />
                      </span>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </ToggleSection>
      </div>

      <div id="section" class="mt-5">
        <div
          class="px-4 py-3 list-title is-flex is-align-items-center is-justify-content-space-between"
        >
          <span class="is-size-5 has-text-weight-semibold">
            Select the Dataset Copies to remove
          </span>
          <div class="field">
            <input
              id="switchRoundedDefault"
              v-model="showDatafiles"
              type="checkbox"
              class="switch is-rounded is-small is-primary"
              checked="showDatafiles"
            />
            <label for="switchRoundedDefault" class="is-size-7 pt-0">
              {{ showDatafiles ? 'Hide' : 'Show' }} Datafiles
            </label>
          </div>
        </div>
        <div class="my-4 px-5 has-text-primary is-size-6">
          <i class="fa-solid fa-info-circle"></i>
          <i>
            Please
            <b>select</b>
            datasets copies that need to be
            <b>removed</b>
            . Datasets files are by default
            <b>removed from all volumes (checkbox is checked)</b>
            . Please uncheck the checkbox to prevent the removal of datasets
            files on the associated volume
          </i>
          <span v-if="!showHelpText" class="learn-more" @click="toggleHelpText">
            learn more
          </span>
          <br />
          <div v-if="showHelpText" class="mt-3">
            Please note that
            <b>you</b>
            can remove a dataset copy only if:
            <ol class="ml-6">
              <li>
                The storage volume allows for data removal; for example, one
                cannot remove from a tape volume (archives)
              </li>
              <li>
                The dataset has its
                <b>removable</b>
                status set to
                <b>TRUE</b>
                . All dataset copies but the
                <b>primary copy</b>
                are removable. The
                <b>primary copy</b>
                can be removed if an archive is available or if the dataset
                quality was set to
                <b>FAILED</b>
                .
              </li>
              <li>
                You have the
                <b>Delete</b>
                permission of the dataset
              </li>
              <span class="learn-more" @click="toggleHelpText">
                (show less)
              </span>
            </ol>
          </div>
        </div>

        <div class="content-item-list-container mx-5 mb-5">
          <table class="table is-fullwidth is-bordered">
            <thead class="sticky at-top">
              <tr>
                <th class="vertically-centered">Dataset</th>
                <th v-show="showDatafiles" class="vertically-centered">
                  Datafiles
                </th>
                <th
                  v-for="storagevolumeId in Object.keys(record.storagevolumes)"
                  :key="storagevolumeId"
                >
                  <div class="is-flex is-flex-direction-column storagevolume">
                    <span
                      class="text-ellipsis"
                      :title="record.storagevolumes[storagevolumeId].name"
                    >
                      {{ record.storagevolumes[storagevolumeId].name }}
                    </span>
                    <span class="_checkbox mt-1" style="text-align: center">
                      <input
                        v-if="
                          record.storagevolumes[storagevolumeId].is_modifyable
                        "
                        type="checkbox"
                        :checked="
                          areAllDatasetsSelectedByStoragevolume[storagevolumeId]
                        "
                        :disabled="
                          !checkIfStoragevolumeDeletable(storagevolumeId)
                        "
                        @change="
                          toggleAllStoragevolumeSelection(
                            storagevolumeId,
                            $event,
                          )
                        "
                      />
                      <span v-else><i>(Read Only)</i></span>
                    </span>
                  </div>
                </th>
              </tr>
            </thead>
            <tfoot class="sticky at-bottom">
              <tr>
                <th :colspan="footerTotalColSpan">Total Selected Datasets</th>
                <th
                  v-for="storagevolumeId in Object.keys(record.storagevolumes)"
                  :key="storagevolumeId"
                  class="has-text-centered"
                >
                  <span
                    v-if="!record.storagevolumes[storagevolumeId].is_modifyable"
                  >
                    -
                  </span>
                  <span v-else>
                    {{
                      (selectedDatasetsByStoragevolume[storagevolumeId] &&
                        selectedDatasetsByStoragevolume[storagevolumeId]
                          .length) ||
                      '-'
                    }}
                  </span>
                </th>
              </tr>
            </tfoot>
            <tbody>
              <template v-for="dataset in datasets" :key="dataset.id">
                <tr
                  v-for="(datafile, datafileIdx) in dataset.datafiles"
                  :key="datafile.id"
                >
                  <template v-if="datafileIdx === 0">
                    <td
                      :rowspan="dataset.datafiles.length"
                      class="vertically-centered"
                    >
                      <div class="is-flex">
                        <span>
                          {{ dataset.name }}
                        </span>
                        <div class="is-flex" style="margin-left: auto">
                          <span
                            v-if="dataset.is_managed"
                            v-tooltip="
                              'This is a managed dataset. It can be removed from a managed storage volume only if it is archived or has a FAILED QC status.'
                            "
                            class="tag is-success mr-1"
                          >
                            <span class="icon has-text-success-light">
                              <i class="fa-solid fa-user-tie" />
                            </span>
                          </span>
                          <div
                            v-if="dataset.qc"
                            class="tags has-addons m-0 mr-1"
                          >
                            <span
                              class="tag m-0 is-light is-small"
                              :class="{
                                'is-success': dataset.qc === 'PASSED',
                                'is-danger': dataset.qc === 'FAILED',
                              }"
                            >
                              QC
                            </span>
                            <span
                              class="tag m-0 is-small"
                              :class="{
                                'is-success': dataset.qc === 'PASSED',
                                'is-danger': dataset.qc === 'FAILED',
                              }"
                            >
                              {{ dataset.qc }}
                            </span>
                          </div>
                          <StatusWidget
                            class="m-0 mr-1"
                            :status="{ value: dataset.status }"
                          ></StatusWidget>
                        </div>
                      </div>
                    </td>
                  </template>
                  <td v-show="showDatafiles">
                    {{ datafile.name }}
                  </td>
                  <template v-if="datafileIdx === 0">
                    <td
                      v-for="storagevolumeId in Object.keys(
                        record.storagevolumes,
                      )"
                      :key="storagevolumeId"
                      :rowspan="dataset.datafiles.length"
                      class="has-text-centered vertically-centered"
                    >
                      <i
                        v-if="
                          record.storagevolumes[storagevolumeId]
                            .is_modifyable &&
                          !checkIfUserHasPermissionToDelete({
                            datafilecopies: datafile.datafilecopies,
                            storagevolumeId,
                          }) &&
                          getNumberOfCopies({ dataset, storagevolumeId }) !== 0
                        "
                        v-tooltip="
                          `You don\'t have permission to delete this dataset`
                        "
                        class="fa-solid fa-lock ml-2 has-text-grey"
                      />
                      <span
                        v-else-if="
                          getNumberOfCopies({ dataset, storagevolumeId }) &&
                          record.storagevolumes[storagevolumeId].is_modifyable
                        "
                        v-tooltip="{
                          content: checkIfDatasetDeletable({
                            datafilecopies: datafile.datafilecopies,
                            storagevolumeId,
                          })
                            ? ''
                            : getDisableCheckboxTooltipText({
                                storagevolumeId,
                                removable: checkIfDatasetDeletable({
                                  datafilecopies: datafile.datafilecopies,
                                  storagevolumeId,
                                }),
                                qc: dataset.qc,
                              }),
                        }"
                        class="_checkbox"
                        style="text-align: center"
                      >
                        <input
                          :disabled="
                            !checkIfDatasetDeletable({
                              datafilecopies: datafile.datafilecopies,
                              storagevolumeId,
                            })
                          "
                          type="checkbox"
                          :checked="
                            selectedStoragevolumesByDatasetId[dataset.id] &&
                            selectedStoragevolumesByDatasetId[
                              dataset.id
                            ].includes(storagevolumeId)
                          "
                          @change="
                            toggleStoragevolumeSelection(
                              {
                                datasetId: dataset.id,
                                storagevolumeId,
                              },
                              $event,
                            )
                          "
                        />
                      </span>
                      <template
                        v-if="
                          getNumberOfCopies({ dataset, storagevolumeId }) &&
                          !record.storagevolumes[storagevolumeId].is_modifyable
                        "
                      >
                        <span
                          class="is-size-6"
                          :class="{
                            'ml-2':
                              record.storagevolumes[storagevolumeId]
                                .is_modifyable,
                          }"
                        >
                          {{ getNumberOfCopies({ dataset, storagevolumeId }) }}
                        </span>
                        <i
                          v-tooltip="{
                            content: `${getNumberOfCopies({
                              dataset,
                              storagevolumeId,
                            })} read-only ${pluralize(
                              'copy',
                              getNumberOfCopies({
                                dataset,
                                storagevolumeId,
                              }),
                            )}`,
                          }"
                          class="fa-regular fa-eye ml-2 has-text-grey"
                        ></i>
                      </template>
                      <template
                        v-else-if="
                          getNumberOfCopies({ dataset, storagevolumeId }) === 0
                        "
                      >
                        <i class="is-size-7">no copy</i>
                      </template>
                    </td>
                  </template>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <div class="content mt-5 pb-5 has-text-grey">
        <blockquote>
          <b>
            Comment on Dataset files Removal
            <span class="has-text-danger">*</span>
          </b>
          <br />
          <div class="is-flex mt-1">
            <div class="select is-normal">
              <select v-model="selectedComment">
                <option value="" disabled>Select a reason</option>
                <option
                  v-for="comment in getPredefinedComments()"
                  :key="comment"
                  :value="comment"
                >
                  {{ comment }}
                </option>
              </select>
            </div>
            <div class="ml-2" style="width: 100%">
              <input
                v-model="writtenComment"
                :class="{
                  'is-danger':
                    selectedComment === 'Other' && writtenComment.length === 0,
                }"
                type="text"
                placeholder="Write your comment..."
                class="input is-normal is-fullwidth"
              />
            </div>
          </div>
          <label v-if="loggedInUser.isSuperUser" class="checkbox mt-4">
            <input v-model="isDryRun" type="checkbox" />
            Enable dry-run
          </label>
        </blockquote>
      </div>
    </div>
  </div>
</template>

<script>
import { keyBy, groupBy } from 'lodash-es'
import pluralize from 'pluralize'
import { mapGetters } from 'vuex'
import ToggleSection from '@/components/fields/ToggleSection.vue'
import StatusWidget from '@/components/StatusWidget.vue'
import { get, post } from '@/api'
import { alphaNumSort } from '@/utils'

const PREDEFINED_COMMENTS = [
  'Cleanup space after the archive',
  'Freeing some storage-volume space',
  'Removing bad quality data',
  'Other',
]

const sortOfflineLast = (a, b) => {
  const aIsOffline = a.status === 'OFFLINE'
  const bIsOffline = b.status === 'OFFLINE'
  if (aIsOffline && !bIsOffline) return 1
  else if (!aIsOffline && bIsOffline) return -1
  else return 0
}

export default {
  name: 'BatchDeleteDatasets',
  components: {
    ToggleSection,
    StatusWidget,
  },
  props: {
    modelName: {
      type: String,
      required: true,
    },
    archiveId: {
      type: String,
      required: false,
    },
    recordsId: {
      type: Array,
      required: false,
    },
  },
  data() {
    return {
      record: null,
      showLoaderHelpText: false,
      selectedStoragevolumesByDatasetId: [],
      areAllDatasetsSelectedByStoragevolume: {},
      studiesByProjectId: null,
      selectedComment: '',
      writtenComment: '',
      showHelpText: false,
      showDatafiles: false,
      isLoading: true,
      isDryRun: false,
    }
  },
  computed: {
    ...mapGetters('auth', ['loggedInUser']),
    datasets() {
      return ([...this.record.datasets] || [])
        .sort((a, b) => alphaNumSort(a.name, b.name))
        .sort(sortOfflineLast)
    },
    studies() {
      return this.record.studies || []
    },
    projects() {
      return this.record.projects || []
    },
    storagevolumes() {
      return this.record.storagevolumes || []
    },
    studiesCount() {
      return Object.keys(this.studies).length
    },
    projectsCount() {
      return Object.keys(this.projects).length
    },
    selectedDatasetsCount() {
      return Object.values(this.selectedStoragevolumesByDatasetId).reduce(
        (count, storagevolume) => (count += storagevolume.length),
        0,
      )
    },
    modifyableStoragevolumes() {
      return Object.values(this.storagevolumes).filter(
        ({ is_modifyable }) => is_modifyable,
      )
    },
    datafilesCount() {
      const datafilesCount = { projects: {}, studies: {} }

      this.datasets.forEach((dataset) => {
        const count = dataset.datafiles.length

        dataset.projects.forEach((projectId) => {
          if (datafilesCount.projects[projectId]) {
            datafilesCount.projects[projectId].datafiles += count
            datafilesCount.projects[projectId].datasets++
          } else {
            datafilesCount.projects[projectId] = {
              datafiles: count,
              datasets: 1,
            }
          }
        })

        dataset.studies.forEach((studyId) => {
          if (datafilesCount.studies[studyId]) {
            datafilesCount.studies[studyId].datafiles += count
            datafilesCount.studies[studyId].datasets++
          } else {
            datafilesCount.studies[studyId] = { datafiles: count, datasets: 1 }
          }
        })
      })

      return datafilesCount
    },
    datafilecopiesByStoragevolume() {
      const datafilecopiesByStoragevolume = {}
      this.datasets.forEach((dataset) => {
        dataset.datafiles.forEach((datafile) => {
          datafile.datafilecopies.forEach((datafilecopy) => {
            if (!datafilecopiesByStoragevolume[datafilecopy.storagevolume]) {
              datafilecopiesByStoragevolume[datafilecopy.storagevolume] = []
            }

            datafilecopiesByStoragevolume[datafilecopy.storagevolume].push(
              datafilecopy,
            )
          })
        })
      })

      return datafilecopiesByStoragevolume
    },
    datasetById() {
      return keyBy(this.datasets, 'id')
    },
    selectedDatasetsByStoragevolume() {
      const selectedDatasetsByStoragevolume = {}
      Object.keys(this.selectedStoragevolumesByDatasetId).forEach(
        (datasetId) => {
          this.selectedStoragevolumesByDatasetId[datasetId]?.forEach(
            (storagevolumeId) => {
              if (!selectedDatasetsByStoragevolume[storagevolumeId]) {
                selectedDatasetsByStoragevolume[storagevolumeId] = []
              }
              selectedDatasetsByStoragevolume[storagevolumeId].push(datasetId)
            },
          )
        },
      )

      return selectedDatasetsByStoragevolume
    },
    deleteButtonStatus() {
      const disabled =
        !this.selectedComment.length ||
        (this.selectedComment === 'Other' && !this.writtenComment.length) ||
        this.selectedDatasetsCount === 0

      let reason = null
      if (disabled) {
        if (this.selectedDatasetsCount === 0) {
          reason = "You haven't selected any datasets to delete"
        } else if (!this.selectedComment.length) {
          reason = 'Please select a reason for this deletion'
        } else if (
          this.selectedComment === 'Other' &&
          !this.writtenComment.length
        ) {
          reason = 'Please put a short comment as a note for this deletion'
        }
      }

      const status = { disabled, reason }
      return status
    },
    footerTotalColSpan() {
      return this.showDatafiles ? 2 : 1
    },
  },
  methods: {
    pluralize,
    toggleTopLevelStoragevolumeSelection(
      { projectId, studyId, storagevolumeId },
      event,
    ) {
      const currentStoragevolume =
        this.studiesByProjectId[projectId].studies[studyId].storagevolumes[
          storagevolumeId
        ]
      currentStoragevolume._selected = event.target.checked

      currentStoragevolume.datasets.forEach((datasetId) => {
        this.updateStoragevolumeSelection({
          mode: currentStoragevolume._selected ? 'delete' : 'add',
          datasetId,
        })
      })
    },
    toggleHelpText() {
      this.showHelpText = !this.showHelpText
    },
    selectAllDatasets({ datasets }) {
      datasets.forEach((dataset) => {
        this.updateStoragevolumeSelection({
          mode: 'add',
          datasetId: dataset.id,
        })
      })
    },
    toggleStoragevolumeSelection({ datasetId, storagevolumeId }, event) {
      this.updateStoragevolumeSelection({
        mode: event.target.checked ? 'add' : 'delete',
        datasetId,
        storagevolumeId,
      })
    },
    toggleAllStoragevolumeSelection(storagevolumeId, event) {
      this.datasets.forEach(({ id: datasetId }) => {
        this.updateStoragevolumeSelection({
          mode: event.target.checked ? 'add' : 'delete',
          datasetId,
          storagevolumeId,
        })
      })
    },
    updateStoragevolumeSelection({ mode, datasetId, storagevolumeId }) {
      const selectedStoragevolumes = new Set(
        this.selectedStoragevolumesByDatasetId[datasetId],
      )

      this.datasetById[datasetId].datafiles.forEach((datafile) => {
        datafile.datafilecopies
          .filter(
            (datafilecopy) =>
              datafilecopy.removable && datafilecopy.allowed.includes('delete'),
          )
          .filter(
            (datafilecopy) =>
              !storagevolumeId ||
              datafilecopy.storagevolume === storagevolumeId,
          )
          .forEach((datafilecopy) => {
            selectedStoragevolumes[mode](datafilecopy.storagevolume)
          })
      })

      this.selectedStoragevolumesByDatasetId = Object.assign(
        {},
        this.selectedStoragevolumesByDatasetId,
        {
          ...this.selectedStoragevolumesByDatasetId,
          [datasetId]: [...selectedStoragevolumes],
        },
      )

      if (storagevolumeId && mode === 'add') {
        const dataset = this.datasetById[datasetId]

        dataset.projects.forEach((projectId) => {
          dataset.studies.forEach((studyId) => {
            this.studiesByProjectId[projectId].studies[studyId].storagevolumes[
              storagevolumeId
            ]._selected = false
          })
        })
      }
    },
    checkIfDatasetDeletable({ datafilecopies, storagevolumeId }) {
      const currentDatafilecopy = datafilecopies.find(
        (datafilecopy) => datafilecopy.storagevolume === storagevolumeId,
      )
      return (
        currentDatafilecopy.allowed.includes('delete') &&
        currentDatafilecopy.removable
      )
    },
    checkIfUserHasPermissionToDelete({ datafilecopies, storagevolumeId }) {
      const currentDatafilecopy = datafilecopies.find(
        (datafilecopy) => datafilecopy.storagevolume === storagevolumeId,
      )

      return currentDatafilecopy?.allowed.includes('delete')
    },
    getDatasets({ studyId, projectId, storagevolumeId }) {
      const datasetIds = []
      this.datasets.forEach((dataset) => {
        if (
          dataset.projects.includes(projectId) &&
          dataset.studies.includes(studyId) &&
          dataset.datafiles[0].datafilecopies
            .filter(
              (datafilecopy) => datafilecopy.storagevolume === storagevolumeId,
            )
            .some((datafilecopy) => datafilecopy.removable)
        ) {
          datasetIds.push(dataset.id)
        }
      })

      return datasetIds
    },
    prepareStudiesByProjectId({ studies }) {
      const studiesByProjectId = groupBy(studies, (_study) => _study.project)

      Object.keys(studiesByProjectId).forEach((projectId) => {
        const studies = studiesByProjectId[projectId].map((study) => ({
          ...study,
          storagevolumes: keyBy(
            this.modifyableStoragevolumes.map((storagevolume) => ({
              ...storagevolume,
              datasets: this.getDatasets({
                studyId: study.id,
                projectId,
                storagevolumeId: storagevolume.id,
              }),
              _selected: false,
            })),
            'id',
          ),
        }))
        studiesByProjectId[projectId] = {
          studies: keyBy(studies, 'id'),
        }
      })

      this.studiesByProjectId = Object.assign(
        {},
        this.studiesByProjectId,
        studiesByProjectId,
      )
    },
    checkIfStoragevolumeDeletable(storagevolumeId) {
      return this.datafilecopiesByStoragevolume[storagevolumeId].some(
        (datafilecopy) => datafilecopy.removable,
      )
    },
    getPredefinedComments() {
      return PREDEFINED_COMMENTS
    },
    getNumberOfCopies({ dataset, storagevolumeId }) {
      const numberOfDatafiles = dataset.datafiles.length
      const numberOfDatafilecopies = dataset.datafiles.reduce(
        (count, datafile) =>
          (count += datafile.datafilecopies.filter(
            (datafilecopy) =>
              datafilecopy.storagevolume === storagevolumeId &&
              !datafilecopy.deleted,
          ).length),
        0,
      )

      const numberOfCopies = Math.ceil(
        numberOfDatafilecopies / numberOfDatafiles,
      )

      return numberOfCopies
    },
    getDisableCheckboxTooltipText({ storagevolumeId, removable, qc }) {
      let tooltipText = null
      if (!this.storagevolumes[storagevolumeId].is_modifyable) {
        tooltipText = 'This is a read-only storage volume!'
      } else if (!removable) {
        const reasons = [
          'it is not archived',
          'its status is OFFLINE',
          qc !== 'FAILED' ? 'its QC is not FAILED' : '',
          'one of its files is already removed',
        ].filter((v) => v)
        const lastRs = reasons.pop()
        const reasonStr = reasons.join(', ') + ' or ' + lastRs
        tooltipText = `A dataset cannot be removed if ${reasonStr}`
      }

      return tooltipText
    },
    preparePayload() {
      const datasets = Object.entries(this.selectedStoragevolumesByDatasetId)
        .filter(([, storageVolumeIds]) => {
          // exclude datasets on read only volumes
          const modifyableVolumes = storageVolumeIds.filter(
            (storagevolumeId) =>
              this.storagevolumes[storagevolumeId]?.is_modifyable,
          )
          return modifyableVolumes.length
        })
        .map(([datasetId, storageVolumeIds]) => {
          if (storageVolumeIds.length) return datasetId
        })
      const datafilecopies = []

      datasets.forEach((datasetId) => {
        const dataset = this.datasetById[datasetId]
        dataset?.datafiles.forEach((datafile) => {
          datafile.datafilecopies
            .filter((datafilecopy) => {
              const storagevolumeIds =
                this.selectedStoragevolumesByDatasetId[datasetId]
              const modifyableVolumes = storagevolumeIds.filter(
                (id) => this.storagevolumes[id]?.is_modifyable,
              )
              return modifyableVolumes.includes(datafilecopy.storagevolume)
            })
            .forEach((datafilecopy) => {
              datafilecopies.push(datafilecopy.id)
            })
        })
      })

      const comment = `${this.selectedComment}${
        this.selectedComment === 'Other' ? ':' : '.'
      } ${this.writtenComment}`

      return {
        ids: datasets,
        datafilecopies,
        comment,
        ...(this.isDryRun && { dryrun: true }),
      }
    },
    checkIfStoragevolumeSelected(storagevolume) {
      storagevolume._selected =
        !this.selectedDatasetsByStoragevolume[storagevolume.id] ||
        this.selectedDatasetsByStoragevolume[storagevolume.id].length === 0

      return storagevolume._selected
    },
    async getDeletableDatafiles() {
      let queryParams = ''
      if (this.archiveId) {
        queryParams += `archive_id=${this.archiveId}`
      } else if (this.recordsId?.length) {
        queryParams =
          'ids=' + this.recordsId.map((recordId) => recordId).join(',')
      }

      let path = `data_management/datasets/removable_datafilecopies/?${queryParams}`

      this.deletableDatafiles = null
      get(path)
        .then((res) => {
          this.record = res.data
          this.selectAllDatasets(res.data)
          this.prepareStudiesByProjectId(res.data)
          this.isLoading = false
        })
        .catch((error) => {
          console.error(error)
          this.$store.commit('notify', {
            type: 'error',
            message:
              error.response.data.message ||
              'Error fetching removable datasets!',
          })
        })
    },
    // eslint-disable-next-line
    async handleDatasetsDeletion() {
      const path = 'data_management/datasets/remove_datafilecopies/'
      const payload = this.preparePayload()
      let deleteRequestSuccesful = false
      try {
        const response = await post(path, { data: payload })
        if (response.status >= 200 && response.status < 300) {
          deleteRequestSuccesful = true
          let message = [
            response.data?.message ||
              'Datasets deletion succesfully requested.',
            'The selected data will be deleted in the background.',
            'You can monitor the status on the user task page.',
          ].join(' ')
          this.$store.commit('notify', {
            message,
            type: 'success',
            duration: 8000,
          })
          if (this.isDryRun) console.table(response.data)
        }
      } catch (error) {
        deleteRequestSuccesful = false
        const message =
          error.response?.data?.message || 'Error: cannot delete the datasets!'
        this.$store.commit('notify', { message, type: 'error' })
      } finally {
        const emitDeleted = this.isDryRun ? false : deleteRequestSuccesful
        this.$emit('deleted', emitDeleted)
      }
    },
  },
  watch: {
    selectedStoragevolumesByDatasetId: {
      deep: true,
      handler() {
        // toggle the header-storagevolume checkbox
        this.modifyableStoragevolumes.forEach(({ id: storagevolumeId }) => {
          const datasets = this.datasets.filter((dataset) =>
            dataset.datafiles.some((datafile) =>
              datafile.datafilecopies.some(
                (datafilecopy) => datafilecopy.removable,
              ),
            ),
          )

          this.areAllDatasetsSelectedByStoragevolume[storagevolumeId] =
            datasets.length
              ? datasets.every(
                  ({ id: datasetId }) =>
                    this.selectedStoragevolumesByDatasetId[datasetId] &&
                    this.selectedStoragevolumesByDatasetId[datasetId].includes(
                      storagevolumeId,
                    ),
                )
              : false
        })
      },
    },
    deleteButtonStatus(status) {
      this.$emit('button-status', status)
    },
  },
  created() {
    setTimeout(() => {
      this.showLoaderHelpText = true
    }, 2500)
  },
  mounted() {
    this.getDeletableDatafiles()
  },
}
</script>

<style scoped>
.batchdelete {
  padding: 0 0.5rem;
}

.batchdelete,
.batchdelete-content {
  height: 100%;
}

.batchdelete-content {
  display: flex;
}

.batchdelete-content {
  flex-direction: column;
}

.batchdelete-content.loading {
  justify-content: center;
  align-items: center;
}

.list-title {
  border-bottom: 1px solid #dbdbdb;
  background-color: var(--color-background-default);
}

.vertically-centered {
  vertical-align: middle;
}

.content-item-list-container {
  border: 1px solid #dbdbdb;
  background-color: var(--color-background-gray);
  max-height: 700px;
  overflow: auto;
}

.content-item-list-container table {
  border-collapse: separate;
}

.related-object-container {
  border: 1px solid #dbdbdb;
  padding: 10px 35px;
  max-height: 145px;
  overflow-y: scroll;
}
.related-object-container li:first-child {
  margin-top: 5px;
}
.related-object-container li {
  margin-bottom: 8px;
}
.related-object-container li:last-child {
  margin-bottom: 12px;
}

.storagevolume {
  width: 100%;
}
.text-ellipsis {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

.sticky {
  position: sticky;
  background-color: #fafafa;
}
.at-top {
  top: 0;
  z-index: 9;
  box-shadow: 0px 2px 5px 0px rgba(165, 165, 165, 0.25);
}
.at-left {
  left: 0;
  box-shadow: 2px 0px 5px 0px rgba(165, 165, 165, 0.25);
}
.at-bottom {
  bottom: 0;
  box-shadow: 0px -2px 5px 0px rgba(165, 165, 165, 0.25);
}

.bg-header {
  background-color: var(--color-background-default);
}

#section {
  border: 1px solid #dbdbdb;
}

.learn-more {
  cursor: pointer;
  font-style: italic;
  font-size: 12px;
  text-decoration: underline;
}
</style>
